import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {Badge} from 'primereact/badge'
import {Message} from 'primereact/message'
import {OverlayPanel} from 'primereact/overlaypanel'
import React, {useEffect, useRef} from 'react'
import {ReactComponent as FaDoctor} from '../../assets/icons/FaDoctor.svg'
import {ReactComponent as FaThermometerHalf} from '../../assets/icons/FaThermometerHalf.svg'
import {ReactComponent as MdOutlineAssignmentTurnedIn} from '../../assets/icons/MdOutlineAssignmentTurnedIn.svg'
import {ReactComponent as AssignPathway} from '../../assets/menu/AssignPathway.svg'
import {ReactComponent as Filter} from '../../assets/menu/Filter.svg'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import {
    filterExplainEvent,
    filterExplainEventTypes,
    getFilterExplainEventKey,
    onFilterModeChangeListener,
    removeFilterExplainChangeListener,
    removeFilterModeChangeListener,
} from './broadcast/FilterEvent'
import {useDatatableContext} from './DataTableContext'

const StyledAdvancedFilterExplaination = ({}) => {
    useSignals()

    const {
        tableType,
        initialAdvancedFilterRequest,
        handleClearFilterRequestByKey,
        handleClearAdvancedFilterRequest,
    } = useDatatableContext()

    const isBeingFiltered = useSignal(
        initialAdvancedFilterRequest
            ? Object.keys(initialAdvancedFilterRequest).length > 0
            : false,
    )

    const currentFilterBy = useSignal({
        pathway: [],
        staticList: [],
        sicknessLevel: [],
        healthCarePractitioners: [],
        advancedFilter:
            initialAdvancedFilterRequest &&
            Object.keys(initialAdvancedFilterRequest).length > 0
                ? Object.entries(initialAdvancedFilterRequest).map(
                      ([key, value]) => ({
                          [key]: value,
                      }),
                  )
                : [],
    })

    const byPathwayRef = useRef(null)
    const byStaticListRef = useRef(null)
    const bySicknessLevelRef = useRef(null)
    const byHealthCarePractitionerRef = useRef(null)
    const byAdvancedFilterRef = useRef(null)

    const advancedFilterContentTypes = {
        unknown: 'unknown',
        byPathway: 'byPathway',
        byStaticList: 'byStaticList',
        bySicknessLevel: 'bySicknessLevel',
        byAdvancedFilter: 'byAdvancedFilter',
        byHealthCarePractitioner: 'byHealthCarePractitioner',
    }

    const handleCurrentFilterMouseEvent = (
        element,
        contentType,
        actionType,
    ) => {
        switch (contentType) {
            case advancedFilterContentTypes.byPathway:
                if (actionType === 'over') {
                    byPathwayRef.current?.show(element)
                } else {
                    byPathwayRef.current?.hide(element)
                }
                break

            case advancedFilterContentTypes.byStaticList:
                if (actionType === 'over') {
                    byStaticListRef.current?.show(element)
                } else {
                    byStaticListRef.current?.hide(element)
                }
                break
            case advancedFilterContentTypes.bySicknessLevel:
                if (actionType === 'over') {
                    bySicknessLevelRef.current?.show(element)
                } else {
                    bySicknessLevelRef.current?.hide(element)
                }
                break
            case advancedFilterContentTypes.byHealthCarePractitioner:
                if (actionType === 'over') {
                    byHealthCarePractitionerRef.current?.show(element)
                } else {
                    byHealthCarePractitionerRef.current?.hide(element)
                }
                break

            case advancedFilterContentTypes.byAdvancedFilter:
                if (actionType === 'over') {
                    byAdvancedFilterRef.current?.show(element)
                } else {
                    byAdvancedFilterRef.current?.hide(element)
                }
                break
            default:
                break
        }
    }

    const iconProps = {
        width: 16,
        height: 16,
    }

    const CurrentFilterItem = ({
        icon: Icon,
        label,
        contentType,
        iconProps,
        badgeNumber,
    }) => {
        const clickRef = useRef(null)
        const handleBadgeMouseOver = () => {
            const badgeRef = document.getElementById(
                `badge-current-filter-${contentType}`,
            )

            const badgeRemoveRef = document.getElementById(
                `badge-remove-current-filter-${contentType}`,
            )

            if (badgeRef && badgeRemoveRef) {
                badgeRef.classList.add('hidden')
                badgeRemoveRef.classList.remove('hidden')
            }
        }

        const handleBadgeMouseLeave = () => {
            const badgeRef = document.getElementById(
                `badge-current-filter-${contentType}`,
            )

            const badgeRemoveRef = document.getElementById(
                `badge-remove-current-filter-${contentType}`,
            )

            if (badgeRef && badgeRemoveRef) {
                badgeRef.classList.remove('hidden')
                badgeRemoveRef.classList.add('hidden')
            }
        }

        const handleBadgeMouseClick = () => {
            clickRef.current = clickRef.current ? false : true

            if (clickRef.current) {
                const badgeWrapper = document.getElementById(
                    `current-filter-${contentType}`,
                )
                badgeWrapper.classList.add('text-darkcyan')
                badgeWrapper.classList.add('bg-white')
            } else {
                const badgeWrapper = document.getElementById(
                    `current-filter-${contentType}`,
                )
                badgeWrapper.classList.remove('text-darkcyan')
                badgeWrapper.classList.remove('bg-white')
            }
        }

        const handleRemoveIconClick = () => {
            switch (contentType) {
                case advancedFilterContentTypes.byPathway:
                    onFilterModeChangeListener(
                        tableType,
                        filterExplainEventTypes.CLEAR_PATHWAY_FILTER,
                    )

                    if (handleClearFilterRequestByKey) {
                        handleClearFilterRequestByKey('pathwayIds', [])
                    }

                    byPathwayRef.current?.hide()

                    currentFilterBy.value = {
                        ...currentFilterBy.value,
                        pathway: [],
                    }

                    checkIfBeingFiltered()

                    break

                case advancedFilterContentTypes.byStaticList:
                    onFilterModeChangeListener(
                        tableType,
                        filterExplainEventTypes.CLEAR_STATIC_LIST_FILTER,
                    )
                    if (handleClearFilterRequestByKey) {
                        handleClearFilterRequestByKey('staticListIds', [])
                    }

                    byStaticListRef.current?.hide()

                    currentFilterBy.value = {
                        ...currentFilterBy.value,
                        staticList: [],
                    }

                    checkIfBeingFiltered()

                    break

                case advancedFilterContentTypes.bySicknessLevel:
                    onFilterModeChangeListener(
                        tableType,
                        filterExplainEventTypes.CLEAR_SICKNESS_LEVEL_FILTER,
                    )
                    if (handleClearFilterRequestByKey) {
                        handleClearFilterRequestByKey('sicknessLevels', [])
                    }

                    bySicknessLevelRef.current?.hide()

                    currentFilterBy.value = {
                        ...currentFilterBy.value,
                        sicknessLevel: [],
                    }

                    checkIfBeingFiltered()

                    break

                case advancedFilterContentTypes.byHealthCarePractitioner:
                    onFilterModeChangeListener(
                        tableType,
                        filterExplainEventTypes.CLEAR_HCP_FILTER,
                    )
                    if (handleClearFilterRequestByKey) {
                        handleClearFilterRequestByKey('hcpIds', [])
                    }

                    byHealthCarePractitionerRef.current?.hide()

                    currentFilterBy.value = {
                        ...currentFilterBy.value,
                        healthCarePractitioners: [],
                    }

                    checkIfBeingFiltered()

                    break

                case advancedFilterContentTypes.byAdvancedFilter:
                    onFilterModeChangeListener(
                        tableType,
                        filterExplainEventTypes.CLEAR_ADVANCED_FILTER,
                    )

                    if (handleClearAdvancedFilterRequest) {
                        handleClearAdvancedFilterRequest()
                    }

                    byAdvancedFilterRef.current?.hide()

                    currentFilterBy.value = {
                        ...currentFilterBy.value,
                        advancedFilter: [],
                    }

                    checkIfBeingFiltered()

                    break

                default:
                    break
            }
        }

        return (
            <div
                id={`current-filter-${contentType}`}
                className="current-filter current-filter-label bg-white"
                onClick={handleBadgeMouseClick}
                onMouseOver={e => {
                    handleCurrentFilterMouseEvent(e, contentType, 'over')
                }}
                onMouseLeave={e => {
                    if (!clickRef.current) {
                        handleCurrentFilterMouseEvent(e, contentType, 'leave')
                    }
                }}>
                <span>
                    <Icon {...iconProps} />
                </span>
                {label}

                <span className="ml-2 cursor-pointer">
                    <Badge
                        id={`badge-current-filter-${contentType}`}
                        className="current-filter-badge"
                        onMouseOver={handleBadgeMouseOver}
                        onMouseLeave={handleBadgeMouseLeave}
                        value={badgeNumber || 0}
                        severity="success"
                    />
                    <Badge
                        id={`badge-remove-current-filter-${contentType}`}
                        className="current-filter-badge m-0 p-0 hidden"
                        onClick={handleRemoveIconClick}
                        onMouseOver={handleBadgeMouseOver}
                        onMouseLeave={handleBadgeMouseLeave}
                        value={
                            <i
                                className="pi pi-times p-0"
                                style={{fontSize: '8px'}}></i>
                        }
                        severity="danger"></Badge>
                </span>
            </div>
        )
    }

    const clearEmptyValues = (array = []) => {
        if (!array) return []

        return array?.filter(item => {
            const key = Object.keys(item)[0]
            return item[key].length > 0
        })
    }

    const checkIfBeingFiltered = () => {
        const pathway = currentFilterBy.peek().pathway
        const staticList = currentFilterBy.peek().staticList
        const sicknessLevel = currentFilterBy.peek().sicknessLevel
        const advancedFilter = currentFilterBy.peek().advancedFilter
        const healthCarePractitioners =
            currentFilterBy.peek().healthCarePractitioners

        if (
            !isArrayAndNotEmpty(pathway) &&
            !isArrayAndNotEmpty(staticList) &&
            !isArrayAndNotEmpty(sicknessLevel) &&
            !isArrayAndNotEmpty(healthCarePractitioners) &&
            !isArrayAndNotEmpty(advancedFilter)
        ) {
            isBeingFiltered.value = false
        } else {
            isBeingFiltered.value = true
        }

        onFilterModeChangeListener(
            tableType,
            isBeingFiltered.peek()
                ? filterExplainEventTypes.HAS_FILTER
                : filterExplainEventTypes.CLEAR_ALL_FILTER,
        )
    }

    useEffect(() => {
        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.ADVANCED_FILTER_EXPLAIN_CHANGE,
            )}`,
            newFilterItems => {
                const clearEmptyValuesNewFilterItems =
                    clearEmptyValues(newFilterItems)

                currentFilterBy.value = {
                    ...currentFilterBy.value,
                    advancedFilter: isArrayAndNotEmpty(
                        clearEmptyValuesNewFilterItems,
                    )
                        ? [...clearEmptyValuesNewFilterItems]
                        : [],
                }

                checkIfBeingFiltered()
            },
        )

        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.PATHWAY_FILTER_EXPLAIN_CHANGE,
            )}`,
            newFilterItems => {
                const clearEmptyValuesNewFilterItems =
                    clearEmptyValues(newFilterItems)

                currentFilterBy.value = {
                    ...currentFilterBy.value,
                    pathway: isArrayAndNotEmpty(clearEmptyValuesNewFilterItems)
                        ? [...clearEmptyValuesNewFilterItems]
                        : [],
                }

                checkIfBeingFiltered()
            },
        )

        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.STATIC_LIST_FILTER_EXPLAIN_CHANGE,
            )}`,
            newFilterItems => {
                const clearEmptyValuesNewFilterItems =
                    clearEmptyValues(newFilterItems)

                currentFilterBy.value = {
                    ...currentFilterBy.value,
                    staticList: isArrayAndNotEmpty(
                        clearEmptyValuesNewFilterItems,
                    )
                        ? [...clearEmptyValuesNewFilterItems]
                        : [],
                }

                checkIfBeingFiltered()
            },
        )

        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.HCP_FILTER_EXPLAIN_CHANGE,
            )}`,
            newFilterItems => {
                const clearEmptyValuesNewFilterItems =
                    clearEmptyValues(newFilterItems)

                currentFilterBy.value = {
                    ...currentFilterBy.value,
                    healthCarePractitioners: isArrayAndNotEmpty(
                        clearEmptyValuesNewFilterItems,
                    )
                        ? [...clearEmptyValuesNewFilterItems]
                        : [],
                }

                checkIfBeingFiltered()
            },
        )

        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.SICKNESS_LEVEL_FILTER_EXPLAIN_CHANGE,
            )}`,
            newFilterItems => {
                const clearEmptyValuesNewFilterItems =
                    clearEmptyValues(newFilterItems)

                currentFilterBy.value = {
                    ...currentFilterBy.value,
                    sicknessLevel: isArrayAndNotEmpty(
                        clearEmptyValuesNewFilterItems,
                    )
                        ? [...clearEmptyValuesNewFilterItems]
                        : [],
                }

                checkIfBeingFiltered()
            },
        )

        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.CLEAR_ALL_FILTER,
            )}`,
            eventType => {
                if (eventType === filterExplainEventTypes.CLEAR_ALL_FILTER) {
                    currentFilterBy.value = {
                        pathway: [],
                        staticList: [],
                        sicknessLevel: [],
                        healthCarePractitioners: [],
                        advancedFilter: [],
                    }

                    isBeingFiltered.value = false
                }
            },
        )

        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.IMPORTED_FILTER_EXPLAIN_CHANGE,
            )}`,
            newFilterItems => {
                const clearEmptyValuesNewFilterItems =
                    clearEmptyValues(newFilterItems)

                currentFilterBy.value = {
                    ...currentFilterBy.value,
                    importFilter: isArrayAndNotEmpty(
                        clearEmptyValuesNewFilterItems,
                    )
                        ? [...clearEmptyValuesNewFilterItems]
                        : [],
                }

                checkIfBeingFiltered()
            },
        )

        return () => {
            removeFilterExplainChangeListener(
                tableType,
                filterExplainEventTypes.ADVANCED_FILTER_EXPLAIN_CHANGE,
            )

            removeFilterExplainChangeListener(
                tableType,
                filterExplainEventTypes.IMPORTED_FILTER_EXPLAIN_CHANGE,
            )

            removeFilterExplainChangeListener(
                tableType,
                filterExplainEventTypes.PATHWAY_FILTER_EXPLAIN_CHANGE,
            )

            removeFilterExplainChangeListener(
                tableType,
                filterExplainEventTypes.STATIC_LIST_FILTER_EXPLAIN_CHANGE,
            )

            removeFilterExplainChangeListener(
                tableType,
                filterExplainEventTypes.SICKNESS_LEVEL_FILTER_EXPLAIN_CHANGE,
            )

            removeFilterExplainChangeListener(
                tableType,
                filterExplainEventTypes.HCP_FILTER_EXPLAIN_CHANGE,
            )

            removeFilterModeChangeListener(
                tableType,
                filterExplainEventTypes.CLEAR_ALL_FILTER,
            )
        }
    }, [])

    const handleGetValueLabelByKey = (key, value) => {
        if (!key) return ''
        let result
        switch (key) {
            case 'dischargeStatus':
                if (!!value) {
                    result = 'Discharged'
                }
                break
            case 'imported':
                result = value
                    ? 'Created within the system'
                    : 'Imported from the other system'
                break
            default:
                result = value ? 'Assigned' : 'Unassigned'
                break
        }

        return result
    }

    return (
        <React.Fragment>
            {isBeingFiltered.value && (
                <div className="flex align-items-center gap-3 mt-3">
                    {/* Current Filter */}
                    <div>
                        <Message
                            className="font-bold mb-2"
                            severity="info"
                            text="Your table view is being filtered by"
                        />
                    </div>
                    <div className="flex">
                        {currentFilterBy.value.pathway?.length > 0 && (
                            <CurrentFilterItem
                                icon={AssignPathway}
                                label="Pathway"
                                contentType={
                                    advancedFilterContentTypes.byPathway
                                }
                                iconProps={iconProps}
                                badgeNumber={
                                    currentFilterBy.value?.pathway?.length
                                }
                            />
                        )}

                        {currentFilterBy.value.staticList?.length > 0 && (
                            <CurrentFilterItem
                                icon={MdOutlineAssignmentTurnedIn}
                                label="Static List"
                                contentType={
                                    advancedFilterContentTypes.byStaticList
                                }
                                iconProps={iconProps}
                                badgeNumber={
                                    currentFilterBy.value?.staticList?.length
                                }
                            />
                        )}

                        {currentFilterBy.value.sicknessLevel?.length > 0 && (
                            <CurrentFilterItem
                                icon={FaThermometerHalf}
                                label="Sickness Level"
                                contentType={
                                    advancedFilterContentTypes.bySicknessLevel
                                }
                                iconProps={iconProps}
                                badgeNumber={
                                    currentFilterBy.value?.sicknessLevel?.length
                                }
                            />
                        )}

                        {currentFilterBy.value.healthCarePractitioners?.length >
                            0 && (
                            <CurrentFilterItem
                                icon={FaDoctor}
                                label="HC.Practitioner"
                                contentType={
                                    advancedFilterContentTypes.byHealthCarePractitioner
                                }
                                iconProps={iconProps}
                                badgeNumber={
                                    currentFilterBy.value
                                        ?.healthCarePractitioners?.length
                                }
                            />
                        )}

                        {currentFilterBy.value.advancedFilter?.length > 0 && (
                            <CurrentFilterItem
                                icon={Filter}
                                label="Advanced Filter"
                                contentType={
                                    advancedFilterContentTypes.byAdvancedFilter
                                }
                                iconProps={iconProps}
                                badgeNumber={
                                    currentFilterBy.value?.advancedFilter
                                        ?.length
                                }
                            />
                        )}
                    </div>
                </div>
            )}

            {/*  Pathway Panel */}
            <OverlayPanel ref={byPathwayRef} showCloseIcon={false}>
                <div>
                    <div>
                        <i className="pi pi-info-circle mr-2"></i>
                        <span className="font-bold">Pathway</span>
                    </div>

                    <small>
                        View standardized treatment plans tailored to specific
                        medical conditions
                    </small>
                </div>
                <hr />
                {currentFilterBy.value?.pathway?.length > 0 && (
                    <React.Fragment>
                        <h6 className="font-bold mb-1">Selected Pathways</h6>
                        <ul>
                            {currentFilterBy.value.pathway?.map(
                                (item, index) => {
                                    if (index < 5) {
                                        return (
                                            <li key={index} className="mb-2">
                                                <span className="font-semibold text-darkcyan capitalized">
                                                    {' '}
                                                    {item?.label}
                                                </span>
                                            </li>
                                        )
                                    }
                                    return null
                                },
                            )}

                            {currentFilterBy.value.pathway?.length > 5 && (
                                <li>
                                    <span className="font-semibold">
                                        {' '}
                                        +{' '}
                                        {currentFilterBy.value.pathway?.length -
                                            5}{' '}
                                        more pathways
                                    </span>
                                </li>
                            )}
                        </ul>
                    </React.Fragment>
                )}
            </OverlayPanel>

            {/* Static List Panel */}
            <OverlayPanel ref={byStaticListRef} showCloseIcon={false}>
                <div>
                    <div>
                        <i className="pi pi-info-circle mr-2"></i>
                        <span className="font-bold">Static List</span>
                    </div>

                    <small>View patients you are following closely</small>
                </div>
                <hr />
                {currentFilterBy.value?.staticList?.length > 0 && (
                    <React.Fragment>
                        <h6 className="font-bold mb-1">
                            Selected Static Lists
                        </h6>
                        <ul>
                            {currentFilterBy.value.staticList?.map(
                                (item, index) => {
                                    if (index < 5) {
                                        return (
                                            <li key={index} className="mb-2">
                                                <span className="font-semibold text-darkcyan capitalized">
                                                    {' '}
                                                    {item?.label}
                                                </span>
                                            </li>
                                        )
                                    }
                                    return null
                                },
                            )}

                            {currentFilterBy.value.staticList?.length > 5 && (
                                <li>
                                    <span className="font-semibold">
                                        {' '}
                                        +{' '}
                                        {currentFilterBy.value.staticList
                                            ?.length - 5}{' '}
                                        more static lists
                                    </span>
                                </li>
                            )}
                        </ul>
                    </React.Fragment>
                )}
            </OverlayPanel>

            {/* Sickness Level Panel */}
            <OverlayPanel ref={bySicknessLevelRef} showCloseIcon={false}>
                <div>
                    <div>
                        <i className="pi pi-info-circle mr-2"></i>
                        <span className="font-bold">Sickness Level</span>
                    </div>

                    <small>
                        View patients based on the severity of their condition
                    </small>
                </div>
                <hr />
                {currentFilterBy.value?.sicknessLevel?.length > 0 && (
                    <React.Fragment>
                        <h6 className="font-bold mb-1">
                            Selected Sickness Levels
                        </h6>
                        <ul>
                            {currentFilterBy.value.sicknessLevel?.map(
                                (item, index) => {
                                    if (index < 5) {
                                        return (
                                            <li key={index} className="mb-2">
                                                <span
                                                    className="font-semibold"
                                                    style={{
                                                        color:
                                                            item?.value ===
                                                            'severe'
                                                                ? 'red'
                                                                : item?.value ===
                                                                  'moderate'
                                                                ? 'orange'
                                                                : item?.value ===
                                                                  'normal'
                                                                ? 'green'
                                                                : 'blue',
                                                    }}>
                                                    {' '}
                                                    <span className="uppercase">
                                                        {item?.value}
                                                    </span>
                                                </span>
                                            </li>
                                        )
                                    }
                                    return null
                                },
                            )}

                            {currentFilterBy.value.sicknessLevel?.length >
                                5 && (
                                <li>
                                    <span className="font-semibold">
                                        {' '}
                                        +{' '}
                                        {currentFilterBy.value.sicknessLevel
                                            ?.length - 5}{' '}
                                        more sickness levels
                                    </span>
                                </li>
                            )}
                        </ul>
                    </React.Fragment>
                )}
            </OverlayPanel>

            {/* HC.Practitioner Panel */}
            <OverlayPanel
                ref={byHealthCarePractitionerRef}
                showCloseIcon={false}>
                <div>
                    <div>
                        <i className="pi pi-info-circle mr-2"></i>
                        <span className="font-bold">HC.Practitioner</span>
                    </div>

                    <small>
                        View patients based on the health care practitioners
                        they are assigned to
                    </small>
                </div>
                <hr />
                <div>
                    {currentFilterBy.value?.healthCarePractitioners?.length >
                        0 && (
                        <React.Fragment>
                            <h6 className="font-bold mb-1">
                                Selected Health Care Practitioners
                            </h6>
                            <ul>
                                {currentFilterBy.value.healthCarePractitioners?.map(
                                    (item, index) => {
                                        if (index < 5) {
                                            return (
                                                <li
                                                    key={index}
                                                    className="mb-2">
                                                    <span className="font-semibold text-darkcyan">
                                                        {' '}
                                                        {item?.label}
                                                    </span>
                                                </li>
                                            )
                                        }
                                        return null
                                    },
                                )}

                                {currentFilterBy.value.healthCarePractitioners
                                    ?.length > 5 && (
                                    <li>
                                        <span className="font-semibold">
                                            {' '}
                                            +{' '}
                                            {currentFilterBy.value
                                                .healthCarePractitioners
                                                ?.length - 5}{' '}
                                            more health care practitioners
                                        </span>
                                    </li>
                                )}
                            </ul>
                        </React.Fragment>
                    )}
                </div>
            </OverlayPanel>

            {/* Advanced Filter Panel */}
            <OverlayPanel
                ref={byAdvancedFilterRef}
                showCloseIcon={false}
                style={{maxHeight: '40vh', overflowY: 'auto'}}>
                <div>
                    <div>
                        <i className="pi pi-info-circle mr-2"></i>
                        <span className="font-bold">Advanced Filter</span>
                    </div>

                    <small>
                        View patients based on custom filter criterias
                    </small>
                </div>
                <hr />
                <div className="pb-5">
                    {currentFilterBy.value?.advancedFilter?.length > 0 && (
                        <ul className="pl-3">
                            {currentFilterBy.value.advancedFilter?.map(
                                (item, index) => {
                                    const key = Object.keys(item)[0] // Get the key of each object
                                    const values = item[key] // Get the values associated with the key
                                    return (
                                        <li
                                            key={item.key + index}
                                            className="mb-2">
                                            <h6 className="font-bold mb-1">
                                                {key
                                                    ? convertKeyToLabel(key)
                                                    : key}{' '}
                                                :
                                            </h6>
                                            <ul>
                                                {typeof values === 'object' &&
                                                    isArrayAndNotEmpty(
                                                        values,
                                                    ) &&
                                                    values?.map(
                                                        (value, idx) => (
                                                            <li>
                                                                <span className="text-darkcyan font-bold">
                                                                    {value &&
                                                                    typeof value ===
                                                                        'string'
                                                                        ? convertKeyToLabel(
                                                                              value,
                                                                          )
                                                                        : typeof value ===
                                                                          'boolean'
                                                                        ? handleGetValueLabelByKey(
                                                                              key,
                                                                              value,
                                                                          )
                                                                        : value}
                                                                </span>
                                                            </li>
                                                        ),
                                                    )}

                                                {typeof values === 'string' && (
                                                    <li>
                                                        <span className="text-darkcyan font-bold">
                                                            {values
                                                                ? convertKeyToLabel(
                                                                      values,
                                                                  )
                                                                : values}
                                                        </span>
                                                    </li>
                                                )}

                                                {typeof values ===
                                                    'boolean' && (
                                                    <li>
                                                        <span className="text-darkcyan font-bold">
                                                            {values
                                                                ? 'Yes'
                                                                : 'No'}
                                                        </span>
                                                    </li>
                                                )}
                                            </ul>
                                        </li>
                                    )
                                },
                            )}
                        </ul>
                    )}
                </div>
            </OverlayPanel>
        </React.Fragment>
    )
}

export default StyledAdvancedFilterExplaination
